import { useCallback, useContext } from 'react';

import { CurrentApplicationContext } from '@/Contexts/CurrentApplicationContext';
import { TokenContext } from '@/Contexts/TokenContext';
import { AttachmentProps, FileType } from '@/models/Attachment';
import { baseUrl } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';
import usePreviewAnswer from './usePreviewAnswer';

interface UploadParams {
  applicationId: number | undefined;
  questionId: number;
  description: string;
  eventId?: number | undefined;
}

export default function useAttachment() {
  const { authResult } = useContext(TokenContext);
  const { customFetch } = useCustomFetch();
  const { currentApplication, isPreviewMode, answers, updateAnswers } =
    useContext(CurrentApplicationContext);

  const { addPreviewAttachmentToAnswer } = usePreviewAnswer({
    isPreviewMode,
    fetchedAnswers: answers,
    fetchedQuestions: null,
    fetchedApplicationId: currentApplication.id,
    updateAnswers,
  });

  const attachmentUrl = `${baseUrl}attachments`;

  const getApplicationAttachments = useCallback(
    async (applicationId: null | number): Promise<AttachmentProps[] | null> => {
      const url = `${attachmentUrl}?applicationId=${applicationId}`;

      const response = await customFetch<AttachmentProps[]>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, attachmentUrl, customFetch]
  );

  const uploadAttachmentToLytho = useCallback(
    async (attachmentId: null | number): Promise<string | null> => {
      const url = `${attachmentUrl}/lytho/${attachmentId}`;

      const response = await customFetch<string>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, attachmentUrl, customFetch]
  );

  const serializeUrlParams = useCallback((info: UploadParams) => {
    const params = new URLSearchParams(
      Object.fromEntries(Object.entries(info).filter(([, v]) => v !== undefined))
    );
    return params;
  }, []);

  const postUploadedData = useCallback(
    async (
      questionId: number,
      file1: FormData,
      uploadName?: string | null,
      eventId?: number | null
    ): Promise<string | null> => {
      if (isPreviewMode) {
        const formFileName = file1.get('fileName');
        const fileNameFromForm =
          typeof formFileName === 'string' ? formFileName : (uploadName ?? 'preview-file');

        const previewAttachment: AttachmentProps = {
          id: -(Date.now() + Math.floor(Math.random() * 1000)),
          applicationId: currentApplication.id,
          questionId,
          eventId: eventId ?? null,
          description: uploadName ?? null,
          fileName: fileNameFromForm,
          uri: fileNameFromForm,
          fileType: FileType.document,
        };

        await addPreviewAttachmentToAnswer({
          questionId,
          attachment: previewAttachment,
          completionValue: 'completed',
        });

        return 'preview-uploaded';
      }

      const allParams: UploadParams = {
        applicationId: currentApplication.id,
        questionId: questionId,
        description: uploadName ?? 'file',
      };

      if (eventId != null) allParams.eventId = eventId;

      const uploadParams = serializeUrlParams(allParams);

      const newUrl = `${attachmentUrl}?${uploadParams}`;

      const response = await customFetch<string>(newUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: file1,
      });

      return response;
    },
    [
      authResult,
      addPreviewAttachmentToAnswer,
      attachmentUrl,
      currentApplication.id,
      customFetch,
      isPreviewMode,
      serializeUrlParams,
    ]
  );

  const postUrlAttachment = useCallback(
    async (questionId: number, url?: string, eventId?: number): Promise<string | null> => {
      if (isPreviewMode) {
        const previewAttachment: AttachmentProps = {
          id: -(Date.now() + Math.floor(Math.random() * 1000)),
          uri: url,
          fileType: FileType.url,
          questionId,
          applicationId: currentApplication.id,
          description: null,
          fileName: url,
          eventId: eventId ?? null,
        };

        await addPreviewAttachmentToAnswer({
          questionId,
          attachment: previewAttachment,
          completionValue: 'completed',
        });

        return 'preview-url-saved';
      }

      const urlAttachment: AttachmentProps = {
        uri: url,
        fileType: FileType.url,
        questionId: questionId,
        applicationId: currentApplication.id,
        description: null,
        fileName: url,
        eventId: eventId,
      };

      const newUrl = `${attachmentUrl}/url`;

      const response = await customFetch<string>(newUrl, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(urlAttachment),
      });

      return response;
    },
    [
      authResult,
      addPreviewAttachmentToAnswer,
      attachmentUrl,
      currentApplication.id,
      customFetch,
      isPreviewMode,
    ]
  );

  return {
    getApplicationAttachments,
    uploadAttachmentToLytho,
    postUploadedData,
    postUrlAttachment,
  };
}
